import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CuratorOffer } from '../utils/curatorOffer';
import {
  accountIsOpenForCampaigns,
  pickInitialSharedOffer,
  syncAllOwnedProfileOffers,
  type LinkedCuratorProfile,
} from '../utils/accountCuratorOffer';
import {
  buildCuratorOfferViewForProfile,
  ensureOfferLibrary,
  findOfferTemplateForProfile,
  reconcileOfferLibraryWithServer,
} from '../utils/curatorOfferLibrary';
import { fetchLinkedCuratorProfiles, fetchProfileOwnedPlaylists, saveAccountCuratorOffers } from '../services/curatorAccountService';
import { getAuthAccountUserId } from '../utils/authAccount';
import { fetchOwnedPlaylists } from '../utils/ownedPlaylists';
import { isNewShapeComplete, isSpotifyConnectedForCurator } from '../utils/curatorOffer';

interface UseAccountCuratorOfferOptions {
  currentUserId: string | null;
}

export const useAccountCuratorOffer = ({ currentUserId }: UseAccountCuratorOfferOptions) => {
  const [acceptCampaigns, setAcceptCampaigns] = useState(false);
  const [linkedProfiles, setLinkedProfiles] = useState<LinkedCuratorProfile[]>([]);
  const [accountUserId, setAccountUserId] = useState<string | null>(null);
  const [curatorOffer, setCuratorOffer] = useState<CuratorOffer | null>(null);
  const [spotifyGateMessage, setSpotifyGateMessage] = useState<string | null>(null);
  const [offerLoading, setOfferLoading] = useState(false);

  const activeProfile = useMemo(
    () => linkedProfiles.find(profile => profile.id === currentUserId) || null,
    [linkedProfiles, currentUserId]
  );
  const activeProfileAcceptsCampaigns = activeProfile?.acceptCampaigns === true;
  const accountHasOpenOffer = accountIsOpenForCampaigns(linkedProfiles);

  const activeProfileOfferLabel = useMemo(() => {
    if (!currentUserId || !activeProfileAcceptsCampaigns) return null;

    const authAccountUserId = accountUserId || getAuthAccountUserId();
    if (authAccountUserId) {
      const library = reconcileOfferLibraryWithServer(authAccountUserId, linkedProfiles);
      const template = findOfferTemplateForProfile(library, currentUserId);
      if (template?.label) return template.label;
    }

    return null;
  }, [accountUserId, activeProfileAcceptsCampaigns, currentUserId, linkedProfiles]);

  const headerOffer = useMemo(() => {
    if (!currentUserId || !activeProfileAcceptsCampaigns) {
      return null;
    }

    const authAccountUserId = accountUserId || getAuthAccountUserId();
    if (authAccountUserId) {
      const library = reconcileOfferLibraryWithServer(authAccountUserId, linkedProfiles);
      const template = findOfferTemplateForProfile(library, currentUserId);
      if (template) {
        const view = buildCuratorOfferViewForProfile(template, currentUserId);
        if (isNewShapeComplete(view)) {
          return view;
        }
      }
    }

    const offer = activeProfile?.curatorOffer;
    return isNewShapeComplete(offer) ? offer : null;
  }, [
    accountUserId,
    activeProfile,
    activeProfileAcceptsCampaigns,
    currentUserId,
    linkedProfiles,
  ]);

  const loadAccountCuratorState = useCallback(async (userProfileObjectId: string) => {
    setOfferLoading(true);
    try {
      const authAccountUserId = getAuthAccountUserId();
      if (!authAccountUserId) return;

      setAccountUserId(authAccountUserId);
      const profiles = await fetchLinkedCuratorProfiles(authAccountUserId);
      ensureOfferLibrary(authAccountUserId, profiles);
      setLinkedProfiles(profiles);

      const isAccepting = accountIsOpenForCampaigns(profiles);
      const sharedOffer = pickInitialSharedOffer(profiles, userProfileObjectId);
      setAcceptCampaigns(isAccepting);
      setCuratorOffer(sharedOffer);

      if (isAccepting && isSpotifyConnectedForCurator()) {
        await syncAllOwnedProfileOffers({
          profiles,
          accountUserId: authAccountUserId,
          activeProfileId: userProfileObjectId,
          sharedOffer,
          fetchOwnedForActive: fetchOwnedPlaylists,
          fetchOwnedForProfile: profileId => fetchProfileOwnedPlaylists(profileId, authAccountUserId),
          saveOffers: saveAccountCuratorOffers,
        });

        const refreshedProfiles = await fetchLinkedCuratorProfiles(authAccountUserId);
        ensureOfferLibrary(authAccountUserId, refreshedProfiles);
        setLinkedProfiles(refreshedProfiles);
        setCuratorOffer(pickInitialSharedOffer(refreshedProfiles, userProfileObjectId));
      }
    } catch (error) {
      console.error('Error loading account curator state:', error);
    } finally {
      setOfferLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    const profile = linkedProfiles.find(item => item.id === currentUserId);
    if (profile && !isSpotifyConnectedForCurator()) {
      setSpotifyGateMessage('Connect your Spotify account to manage campaigns.');
    }
  }, [currentUserId, linkedProfiles]);

  return {
    acceptCampaigns,
    accountHasOpenOffer,
    accountUserId,
    activeProfile,
    activeProfileAcceptsCampaigns,
    activeProfileOfferLabel,
    curatorOffer,
    headerOffer,
    linkedProfiles,
    loadAccountCuratorState,
    offerLoading,
    setSpotifyGateMessage,
    spotifyGateMessage,
  };
};
